import type { Entry } from 'contentful';

import { HeaderData } from '../models';
import { TypeHeaderSkeleton } from '../models/cms';

import { transformAccountPopover, transformLinkSection, transformMegaNav, transformMobileMenu } from '.';

export const transformHeader = (entry: Entry<TypeHeaderSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>): HeaderData => {
  if (!entry.fields.accountPopover) {
    throw new Error(`Expected account popover in header: ${entry.sys.id}`);
  }

  if (!entry.fields.mobileAccountPopover) {
    throw new Error(`Expected mobile account popover in header: ${entry.sys.id}`);
  }

  if (!entry.fields.mobileMenuManageAviosLinks) {
    throw new Error(`Expected mobile manage avios links in header: ${entry.sys.id}`);
  }

  if (!entry.fields.programmeLabel) {
    throw new Error(`Expected programme label in header: ${entry.sys.id}`);
  }

  if (entry.fields.programmeLogo && !entry.fields.programmeLogo.fields.file) {
    throw new Error(`Expected programme logo in header: ${entry.sys.id}`);
  }

  if (entry.fields.programmeLogo && !entry.fields.programmeLogo.fields.description) {
    throw new Error(`Expected programme logo description/alt in header: ${entry.sys.id}`);
  }

  if (!entry.fields.logo || !entry.fields.logo.fields.file) {
    throw new Error(`Expected logo in header: ${entry.sys.id}`);
  }

  if (!entry.fields.logo.fields.description) {
    throw new Error(`Expected logo description/alt in header: ${entry.sys.id}`);
  }

  const nav = entry.fields.nav.filter((x) => !!x).map(transformMegaNav);

  if (nav.length < 1) {
    throw new Error(`Expected at least 1 nav item in header: ${entry.sys.id}`);
  }

  const mobileNav = entry.fields.mobileNav && transformMobileMenu(entry.fields.mobileNav);

  return {
    logo: {
      alt: entry.fields.logo.fields.description,
      ariaLabel: entry.fields.logoAriaLabel,
      image: entry.fields.logo.fields.file.url,
      url: entry.fields.logoUrl,
    },
    programmeLabel: entry.fields.programmeLabel,
    programmeLogo: entry.fields.programmeLogo && {
      image: entry.fields.programmeLogo.fields.file!.url,
      alt: entry.fields.programmeLogo.fields.description!,
    },
    helpCentre: {
      url: entry.fields.helpCentreUrl,
      label: entry.fields.helpCentreLabel,
    },
    account: transformAccountPopover(entry.fields.accountPopover),
    mobileNav: {
      label: 'Main menu',
      children: mobileNav,
    },
    nav,
    registerLabel: entry.fields.registerLabel,
    registerUrl: entry.fields.registerUrl,
    signInLabel: entry.fields.signInLabel,
    burgerMenuOpenAriaLabel: entry.fields.mobileBurgerMenuAriaLabel,
    burgerMenuCloseAriaLabel: entry.fields.mobileBurgerMenuCloseButtonAriaLabel,
    accountPopoverAriaLabel: entry.fields.accountPopoverAriaLabel,
    mobileAccount: transformAccountPopover(entry.fields.mobileAccountPopover),
    mobileManageAviosLinks: transformLinkSection(entry.fields.mobileMenuManageAviosLinks),
  };
};
